import omit from 'lodash/omit';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { delay, withRetryOn429 } from '../../util/sdkRetry';

const { UUID } = sdkTypes;

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 50;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/ManageListingsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/ManageListingsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/ManageListingsPage/DISCARD_DRAFT_ERROR';

export const DUPLICATE_LISTING_REQUEST = 'app/ManageListingsPage/DUPLICATE_LISTING_REQUEST';
export const DUPLICATE_LISTING_SUCCESS = 'app/ManageListingsPage/DUPLICATE_LISTING_SUCCESS';
export const DUPLICATE_LISTING_ERROR = 'app/ManageListingsPage/DUPLICATE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const SELECT_LISTING = 'app/ManageListingsPage/SELECT_LISTING';
export const DESELECT_LISTING = 'app/ManageListingsPage/DESELECT_LISTING';
export const SELECT_ALL_ON_PAGE = 'app/ManageListingsPage/SELECT_ALL_ON_PAGE';
export const CLEAR_SELECTION = 'app/ManageListingsPage/CLEAR_SELECTION';
export const TOGGLE_SHOW_SELECTED = 'app/ManageListingsPage/TOGGLE_SHOW_SELECTED';
export const RESTORE_SELECTION = 'app/ManageListingsPage/RESTORE_SELECTION';

export const BULK_ACTION_REQUEST = 'app/ManageListingsPage/BULK_ACTION_REQUEST';
export const BULK_ACTION_PROGRESS = 'app/ManageListingsPage/BULK_ACTION_PROGRESS';
export const BULK_ACTION_COMPLETE = 'app/ManageListingsPage/BULK_ACTION_COMPLETE';
export const BULK_ACTION_CANCEL = 'app/ManageListingsPage/BULK_ACTION_CANCEL';
export const BULK_ACTION_RESET = 'app/ManageListingsPage/BULK_ACTION_RESET';

// ================ Reducer ================ //

// SessionStorage helpers for selection persistence
const SELECTION_STORAGE_KEY = 'ManageListingsPage_selectedIds';

const saveSelectionToSession = ids => {
  try {
    sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // sessionStorage may be unavailable
  }
};

const loadSelectionFromSession = () => {
  try {
    const stored = sessionStorage.getItem(SELECTION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
};

const initialState = {
  pagination: null,
  queryParams: null,
  // The latest query asked for (searches go out as the seller types): an older answer
  // arriving after it is dropped.
  queryRequest: 0,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
  duplicatingListing: null,
  duplicatingListingError: null,
  // Selection state. The selection is kept in this tab's session storage, so it outlives a
  // reload; it is read in the browser once (restoreSelection), after the page has rendered.
  selectedListingIds: [],
  selectionRestored: false,
  showSelected: false,
  // Bulk action state
  bulkActionInProgress: false,
  bulkActionType: null,
  bulkActionCurrentIndex: 0,
  bulkActionTotalCount: 0,
  bulkActionResults: [],
  bulkActionCancelled: false,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryRequest: action.meta?.request ?? state.queryRequest,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      if (action.meta?.request !== state.queryRequest) return state;
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      if (action.meta?.request !== state.queryRequest) return state;
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS: {
      const { listingId, response } = payload;
      // Remove the discarded listing from currentPageResultIds
      const currentPageResultIds = state.currentPageResultIds.filter(
        id => id.uuid !== listingId.uuid
      );
      // Update pagination total count
      const pagination = state.pagination
        ? { ...state.pagination, totalItems: state.pagination.totalItems - 1 }
        : null;
      return {
        ...merge(state, response),
        currentPageResultIds,
        pagination,
        discardingDraft: null,
      };
    }
    case DISCARD_DRAFT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };
    }

    case DUPLICATE_LISTING_REQUEST:
      return {
        ...state,
        duplicatingListing: payload.listingId,
        duplicatingListingError: null,
      };
    case DUPLICATE_LISTING_SUCCESS: {
      const { response } = payload;
      return {
        ...merge(state, response),
        currentPageResultIds: resultIds(response.data),
        pagination: response.data.meta,
        duplicatingListing: null,
      };
    }
    case DUPLICATE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        duplicatingListing: null,
        duplicatingListingError: {
          listingId: state.duplicatingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case SELECT_LISTING: {
      const ids = state.selectedListingIds.includes(payload)
        ? state.selectedListingIds
        : [...state.selectedListingIds, payload];
      saveSelectionToSession(ids);
      return { ...state, selectedListingIds: ids };
    }
    case DESELECT_LISTING: {
      const ids = state.selectedListingIds.filter(id => id !== payload);
      saveSelectionToSession(ids);
      return { ...state, selectedListingIds: ids, showSelected: ids.length === 0 ? false : state.showSelected };
    }
    case SELECT_ALL_ON_PAGE: {
      const pageIds = payload;
      const existing = new Set(state.selectedListingIds);
      pageIds.forEach(id => existing.add(id));
      const ids = [...existing];
      saveSelectionToSession(ids);
      return { ...state, selectedListingIds: ids };
    }
    case CLEAR_SELECTION: {
      saveSelectionToSession([]);
      return { ...state, selectedListingIds: [], showSelected: false };
    }
    case TOGGLE_SHOW_SELECTED:
      return { ...state, showSelected: !state.showSelected };
    case RESTORE_SELECTION:
      // Only the first read counts: after it, this state is newer than the storage
      return state.selectionRestored
        ? state
        : { ...state, selectedListingIds: payload, selectionRestored: true };

    case BULK_ACTION_REQUEST:
      return {
        ...state,
        bulkActionInProgress: true,
        bulkActionType: payload.actionType,
        bulkActionCurrentIndex: 0,
        bulkActionTotalCount: payload.totalCount,
        bulkActionResults: [],
        bulkActionCancelled: false,
      };
    case BULK_ACTION_PROGRESS:
      return {
        ...state,
        bulkActionCurrentIndex: payload.index + 1,
        bulkActionResults: [...state.bulkActionResults, payload.result],
      };
    case BULK_ACTION_COMPLETE:
      return { ...state, bulkActionInProgress: false };
    case BULK_ACTION_CANCEL:
      return { ...state, bulkActionCancelled: true };
    case BULK_ACTION_RESET:
      return {
        ...state,
        bulkActionInProgress: false,
        bulkActionType: null,
        bulkActionCurrentIndex: 0,
        bulkActionTotalCount: 0,
        bulkActionResults: [],
        bulkActionCancelled: false,
      };

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = (listingId, response) => ({
  type: DISCARD_DRAFT_SUCCESS,
  payload: { listingId, response },
});

export const discardDraftError = e => ({
  type: DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const duplicateListingRequest = listingId => ({
  type: DUPLICATE_LISTING_REQUEST,
  payload: { listingId },
});

export const duplicateListingSuccess = response => ({
  type: DUPLICATE_LISTING_SUCCESS,
  payload: { response },
});

export const duplicateListingError = e => ({
  type: DUPLICATE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = (queryParams, request) => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
  meta: { request },
});

export const queryListingsSuccess = (response, request) => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
  meta: { request },
});

export const queryListingsError = (e, request) => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

// Selection action creators
export const selectListing = listingId => ({ type: SELECT_LISTING, payload: listingId });
export const deselectListing = listingId => ({ type: DESELECT_LISTING, payload: listingId });
export const selectAllOnPage = listingIds => ({ type: SELECT_ALL_ON_PAGE, payload: listingIds });
export const clearSelection = () => ({ type: CLEAR_SELECTION });
export const toggleShowSelected = () => ({ type: TOGGLE_SHOW_SELECTED });

// Reads the selection kept in session storage. Browser only: the server has no storage, and
// the page it renders starts with nothing selected.
export const restoreSelection = () => dispatch => {
  if (typeof window === 'undefined') return;
  dispatch({ type: RESTORE_SELECTION, payload: loadSelectionFromSession() });
};

// Bulk action creators
export const bulkActionRequest = (actionType, totalCount) => ({
  type: BULK_ACTION_REQUEST,
  payload: { actionType, totalCount },
});
export const bulkActionProgress = (index, result) => ({
  type: BULK_ACTION_PROGRESS,
  payload: { index, result },
});
export const bulkActionComplete = () => ({ type: BULK_ACTION_COMPLETE });
export const cancelBulkAction = () => ({ type: BULK_ACTION_CANCEL });
export const resetBulkAction = () => ({ type: BULK_ACTION_RESET });

// Throwing error for new (loadData may need that info)
let queryRequests = 0;
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  queryRequests += 1;
  const request = queryRequests;
  dispatch(queryListingsRequest(queryParams, request));

  // Strip URL-only params before sending to SDK; keep them in redux for UI use.
  // `keywords` is a valid SDK param and stays.
  const { tab: _tab, perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response, request));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e), request));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const discardDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));

  const { queryParams } = getState().ManageListingsPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Re-query listings to get updated list
      return sdk.ownListings.query(queryParams);
    })
    .then(response => {
      dispatch(discardDraftSuccess(listingId, response));
      return response;
    })
    .catch(e => {
      dispatch(discardDraftError(storableError(e)));
    });
};

// What a listing's publicData says about the listing itself rather than the item: a new
// draft starts without it (in stock and searchable once published, like any new listing)
const LISTING_STATE_KEYS = ['soldOut', 'stock'];

/**
 * Copies a listing into a new draft: its words, price, location and details. Photos and
 * stock are set as the draft is published; `copyTitle` names the copy from the title.
 */
export const duplicateListing = (listingId, copyTitle) => (dispatch, getState, sdk) => {
  dispatch(duplicateListingRequest(listingId));

  const { queryParams } = getState().ManageListingsPage;

  // First, fetch the source listing with all data
  return sdk.ownListings
    .show({
      id: listingId,
      include: ['currentStock'],
    })
    .then(response => {
      const listing = response.data.data;
      const { title, description, price, geolocation, publicData } = listing.attributes;

      // Build draft data, only including fields that have values
      // Note: Images cannot be copied directly - they're tied to the original listing
      // Note: Stock cannot be set on drafts - user must set it when publishing
      const draftData = {
        title: copyTitle(title),
      };

      if (description) {
        draftData.description = description;
      }
      if (price) {
        draftData.price = price;
      }
      if (geolocation) {
        draftData.geolocation = geolocation;
      }
      const itemData = omit(publicData || {}, LISTING_STATE_KEYS);
      if (Object.keys(itemData).length > 0) {
        draftData.publicData = itemData;
      }

      // Create draft with copied data
      return sdk.ownListings.createDraft(draftData, { expand: true });
    })
    .then(() => {
      // Re-query to update the list, ensuring currentStock is included
      return sdk.ownListings.query({
        ...queryParams,
        include: ['images', 'currentStock'],
      });
    })
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(duplicateListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(duplicateListingError(storableError(e)));
    });
};

export const bulkPublishListings = listingIds => async (dispatch, getState, sdk) => {
  dispatch(bulkActionRequest('publish', listingIds.length));

  for (let i = 0; i < listingIds.length; i++) {
    if (getState().ManageListingsPage.bulkActionCancelled) break;

    const listingId = listingIds[i];
    try {
      const response = await withRetryOn429(() =>
        sdk.ownListings.publishDraft({ id: new UUID(listingId) }, { expand: true })
      );
      dispatch(addOwnEntities({ data: { data: response.data.data, included: [] } }));
      dispatch(bulkActionProgress(i, { listingId, status: 'success' }));
    } catch (e) {
      const errorMessage = e?.data?.errors?.[0]?.title || e?.message || 'Unknown error';
      dispatch(bulkActionProgress(i, { listingId, status: 'error', error: errorMessage }));
    }

    if (i < listingIds.length - 1) await delay(500);
  }

  dispatch(bulkActionComplete());
};

export const bulkCloseListings = listingIds => async (dispatch, getState, sdk) => {
  dispatch(bulkActionRequest('close', listingIds.length));

  for (let i = 0; i < listingIds.length; i++) {
    if (getState().ManageListingsPage.bulkActionCancelled) break;

    const listingId = listingIds[i];
    try {
      const response = await withRetryOn429(() =>
        sdk.ownListings.close({ id: new UUID(listingId) }, { expand: true })
      );
      dispatch(addOwnEntities({ data: { data: response.data.data, included: [] } }));
      dispatch(bulkActionProgress(i, { listingId, status: 'success' }));
    } catch (e) {
      const errorMessage = e?.data?.errors?.[0]?.title || e?.message || 'Unknown error';
      dispatch(bulkActionProgress(i, { listingId, status: 'error', error: errorMessage }));
    }

    if (i < listingIds.length - 1) await delay(500);
  }

  dispatch(bulkActionComplete());
};

export const bulkOpenListings = listingIds => async (dispatch, getState, sdk) => {
  dispatch(bulkActionRequest('open', listingIds.length));

  for (let i = 0; i < listingIds.length; i++) {
    if (getState().ManageListingsPage.bulkActionCancelled) break;

    const listingId = listingIds[i];
    try {
      const response = await withRetryOn429(() =>
        sdk.ownListings.open({ id: new UUID(listingId) }, { expand: true })
      );
      dispatch(addOwnEntities({ data: { data: response.data.data, included: [] } }));
      dispatch(bulkActionProgress(i, { listingId, status: 'success' }));
    } catch (e) {
      const errorMessage = e?.data?.errors?.[0]?.title || e?.message || 'Unknown error';
      dispatch(bulkActionProgress(i, { listingId, status: 'error', error: errorMessage }));
    }

    if (i < listingIds.length - 1) await delay(500);
  }

  dispatch(bulkActionComplete());
};

// Map tab values to Sharetribe listing states for filtering
const TAB_STATES = {
  all: undefined,
  draft: ['draft'],
  active: ['published'],
  closed: ['closed'],
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  const tab = queryParams.tab || 'active';
  const keywords = queryParams.keywords?.trim();

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const states = TAB_STATES[tab];

  return queryOwnListings({
    ...queryParams,
    tab,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'currentStock'],
    ...(states ? { states } : {}),
    ...(keywords ? { keywords } : {}),
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  });
};
